import { PartilhaResultado } from 'src/drheranca/types';
import { resolveItcmdAmounts } from 'src/drheranca/services/partilhaTotalLiquidoBreakdown';
import type { RowTooltipGuide } from '../types';

export type PartilhaMatrixShareKey =
  | 'Meação' | 'Herança' | 'Doação' | 'Legado' | 'Parte disponível' | 'Usufruto' | 'Nua-propriedade'
  | 'subtotal' | 'itcmd_cm' | 'itcmd_doacao';

export interface PartilhaMatrixRowDef {
  key: PartilhaMatrixShareKey;
  label: string;
  guide: RowTooltipGuide;
  subtitle?: string;
  excelDescription?: string;
  baseLabel?: string;
  getValue: (p: PartilhaResultado) => number;
  getBaseValue?: (p: PartilhaResultado) => number;
  isDeduction?: boolean;
  isSubtotal?: boolean;
  isInfoOnly?: boolean;
  hideIfEmpty?: boolean;
}

/** Descrição curta para exportação Excel (coluna B). */
export function partilhaRowExcelDescription(row: PartilhaMatrixRowDef): string {
  return row.excelDescription ?? row.subtitle ?? row.label;
}

export const PARTILHA_MATRIX_ROW_DEFS: PartilhaMatrixRowDef[] = [
  {
    key: 'Meação',
    label: 'Meação',
    guide: {
      paragraphs: [
        'Parcela do patrimônio comum atribuída ao cônjuge/companheiro meeiro (em regra metade dos bens comuns).',
      ],
      bullets: [
        'No modo de partilha amigável com usufruto, esta linha contabiliza apenas os bens recebidos integralmente em pagamento da meação.',
        'O usufruto vitalício do imóvel é exibido na linha "Usufruto" logo abaixo, para não duplicar.',
      ],
    },
    subtitle: 'Bens recebidos integralmente pelo meeiro',
    baseLabel: 'data óbito (legal) · atualizado (acordo)',
    getValue: (p) =>
      (p.assetShares || [])
        .filter((s) => s.type === 'Meação')
        .reduce((sum, s) => sum + (s.value || 0), 0),
    hideIfEmpty: true,
  },
  {
    key: 'Usufruto',
    label: 'Usufruto vitalício (do cônjuge)',
    guide: {
      paragraphs: [
        'No modo de partilha amigável (acordo com doação derivada e usufruto), parcela do imóvel atribuída ao cônjuge como usufruto vitalício.',
      ],
      bullets: [
        'Base usual: ⅓ do valor venal atualizado do imóvel (SP).',
        'A nua-propriedade correspondente vai ao herdeiro na linha homônima.',
      ],
    },
    subtitle: 'Fração do imóvel reservada ao cônjuge meeiro',
    baseLabel: 'valor venal atualizado',
    getValue: (p) => (p.assetShares || []).filter((s) => s.type === 'Usufruto').reduce((sum, s) => sum + (s.value || 0), 0),
    hideIfEmpty: true,
  },
  {
    key: 'Herança',
    label: 'Herança',
    guide: {
      paragraphs: [
        'Valor da quota da herança líquida (legítima), avaliada na data do óbito.',
      ],
      bullets: ['Em termos simples: a parte da herança reservada por lei ao herdeiro necessário.'],
    },
    subtitle: 'Quota da herança reservada por lei',
    baseLabel: 'data do óbito',
    getValue: (p) => (p.assetShares || []).filter((s) => s.type === 'Herança').reduce((sum, s) => sum + (s.value || 0), 0),
    hideIfEmpty: true,
  },
  {
    key: 'Nua-propriedade',
    label: 'Nua-propriedade (do herdeiro)',
    guide: {
      paragraphs: [
        'No modo de partilha amigável, parcela do imóvel atribuída ao herdeiro como nua-propriedade.',
      ],
      bullets: [
        'Base usual: ⅔ do valor venal atualizado (SP).',
        'Combina herança causa mortis e doação derivada do cônjuge, sem duplicar na linha de doação em vida.',
      ],
    },
    subtitle: 'Fração do imóvel transmitida ao herdeiro',
    baseLabel: 'valor venal atualizado',
    getValue: (p) => (p.assetShares || []).filter((s) => s.type === 'Nua-propriedade').reduce((sum, s) => sum + (s.value || 0), 0),
    hideIfEmpty: true,
  },
  {
    key: 'Doação',
    label: 'Doação em vida (recebida)',
    guide: {
      paragraphs: [
        'Liberalidades cadastradas (doações em vida) recebidas por esta pessoa, pelo valor venal atualizado.',
      ],
      bullets: [
        'Soma no quinhão bruto recebido (Parte 1 da matriz).',
        'O ITCMD sobre essas liberalidades aparece na dedução "ITCMD liberalidades".',
        'Não inclui doação derivada do modo acordo (já embutida em "Nua-propriedade").',
      ],
    },
    subtitle: 'Liberalidades já recebidas em vida',
    baseLabel: 'valor venal atualizado',
    getValue: (p) => {
      const fromShares = (p.assetShares || [])
        .filter((s) => s.type === 'Doação')
        .reduce((sum, s) => sum + (s.value || 0), 0);
      if (fromShares > 0.005) return fromShares;
      // Fallback: donatários que não são herdeiros necessários não recebem assetShares
      // do motor, mas aparecem em partilhaPorHerdeiro com valorRecebidoBruto = soma das
      // doações em vida. Sem este fallback, a linha mostraria 0 e o subtotal não bateria.
      const hasNoShares = !(p.assetShares?.length);
      return hasNoShares ? (p.valorRecebidoBruto ?? 0) : 0;
    },
    hideIfEmpty: true,
  },
  {
    key: 'Legado',
    label: 'Legado',
    guide: {
      paragraphs: ['Bens ou quantias deixados por testamento a legatários.'],
      bullets: ['Avaliados na data do óbito (base do ITCMD causa mortis).'],
    },
    subtitle: 'Bens ou valores deixados por testamento',
    baseLabel: 'data do óbito',
    getValue: (p) => (p.assetShares || []).filter(s => s.type === 'Legado').reduce((sum, s) => sum + (s.value || 0), 0),
    hideIfEmpty: true,
  },
  {
    key: 'Parte disponível',
    label: 'Parte disponível',
    guide: {
      paragraphs: ['Quota da parte disponível deixada por testamento.'],
      bullets: [
        'Destinada a herdeiros testamentários ou outros beneficiários, conforme o testamento.',
        'Avaliada na data do óbito.',
      ],
    },
    subtitle: 'Valor deixado por testamento',
    baseLabel: 'data do óbito',
    getValue: (p) => (p.assetShares || []).filter(s => s.type === 'Parte disponível').reduce((sum, s) => sum + (s.value || 0), 0),
    hideIfEmpty: true,
  },
  {
    key: 'subtotal',
    label: 'Subtotal — quinhão bruto recebido',
    guide: {
      paragraphs: [
        'Soma da Parte 1 da matriz: tudo o que integra o quinhão bruto de cada beneficiário.',
      ],
      bullets: [
        'Inclui meação, usufruto, herança, nua-propriedade, doações em vida, legado e parte disponível.',
        'É o valor bruto antes das deduções (ITCMD, emolumentos, RI e honorários).',
      ],
    },
    subtitle: 'Soma da Parte 1 (antes das deduções)',
    getValue: (p) => p.valorRecebidoBruto ?? 0,
    isSubtotal: true,
  },
  {
    key: 'itcmd_cm',
    label: 'ITCMD causa mortis (−)',
    guide: {
      paragraphs: [
        'Parcela do ITCMD sobre transmissão causa mortis (herança, legados, parte disponível e nua-propriedade).',
      ],
      bullets: [
        'Alíquota fixa de 4% (SP) sobre a base tributável à data do óbito.',
        'Isenções da Lei 10.705/00 art. 6º, I (depósitos até 1.000 UFESP, imóvel residencial único, etc.) ainda não são aplicadas automaticamente pelo sistema.',
      ],
    },
    subtitle: 'Sobre transmissão causa mortis',
    baseLabel: 'data do óbito · alíquota 4% SP',
    getValue: (p) => resolveItcmdAmounts(p).itcmdCm,
    getBaseValue: (p) => p.itcmdBaseCausaMortisTributavel ?? p.itcmdBaseCausaMortis ?? 0,
    isDeduction: true,
  },
  {
    key: 'itcmd_doacao',
    label: 'ITCMD liberalidades (−)',
    guide: {
      paragraphs: [
        'Parcela do ITCMD sobre liberalidades (doações em vida e doação derivada em partilha amigável).',
      ],
      bullets: [
        'Alíquota fixa de 4% (SP) sobre a base de liberalidades.',
        'Isenção por UFESP aplicada na soma anual de cada par doador/donatário.',
        'A doação derivada do modo acordo entra na base, embora esteja embutida na linha "Nua-propriedade".',
      ],
    },
    subtitle: 'Sobre liberalidades',
    baseLabel: 'valor venal atualizado · alíquota 4% SP',
    getValue: (p) => resolveItcmdAmounts(p).itcmdDoacao,
    getBaseValue: (p) => p.itcmdBaseDoacaoBruta ?? 0,
    isDeduction: true,
  },
];
